import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import {
  Button,
  ListItem,
  ListItemText,
  IconButton,
  Typography,
  Box,
  Paper,
  Grid,
} from '@mui/material'
import { v4 as uuidv4 } from 'uuid'
import MyButton from '../../../components/buttons'
import MySelect from '../../../components/MySelect'
import { FaCog } from 'react-icons/fa'
import MyInput from '../../../components/MyInput'
import { MdEuro } from 'react-icons/md'
import { DeleteIcon } from 'lucide-react'

interface UserProfileFormProps {
  formData: any
  handleFieldChange: (key: string, value: any) => void
}

interface FormData {
  price: number[]
  timing: string[]
  priceEscort: number[]
  timingEscort: string[]
  showEscortPrices: boolean
}

const UserPricesForm = ({
  formData: initialFormData,
  handleFieldChange: propHandleFieldChange,
}: UserProfileFormProps) => {
  const { t } = useTranslation()

  const [formData, setFormData] = useState<FormData>(() => {
    const currentPath = window.location.pathname
    let storageKey = 'myAnnounce'

    if (currentPath === '/dashboard/announce-work/') {
      storageKey = 'myJobOffer'
    } else if (currentPath === '/dashboard/announce-event/') {
      storageKey = 'myEvent'
    } else if (currentPath === '/dashboard/announce-establishment/') {
      storageKey = 'myEstablishment'
    }

    const savedData = JSON.parse(localStorage.getItem(storageKey) || '{}')
    const showEscortPrices =
      savedData.status && savedData.status.includes('escort')

    return {
      ...initialFormData,
      ...savedData,
      price: savedData.price || [],
      timing: savedData.timing || [],
      priceEscort: savedData.priceEscort || [],
      timingEscort: savedData.timingEscort || [],
      showEscortPrices,
    }
  })

  const [isAddingPrivatePrice, setIsAddingPrivatePrice] = useState(false)
  const [isAddingEscortPrice, setIsAddingEscortPrice] = useState(false)
  const [newPriceEntry, setNewPriceEntry] = useState({
    duration: '',
    price: 0,
  })

  const availableDurations = [
    '15min',
    '30min',
    '45min',
    '1h',
    '1h30',
    '2h',
    '2h30',
    '3h',
    '3h30',
    '4h',
    'evening',
    'night',
    'week-end',
  ].filter(duration => !formData.timing.includes(duration))

  const availableAwayDurations = [
    ...availableDurations,
    'trip-costs-25km',
    'trip-costs-50km',
    'trip-costs-75km',
    'trip-costs-100km',
  ].filter(duration => !formData.timingEscort.includes(duration))

  const options = availableDurations.map(duration => ({
    value: duration,
    label: t(`create-ad.${duration}`),
  }))

  const awayOptions = availableAwayDurations.map(duration => ({
    value: duration,
    label: t(`create-ad.${duration}`),
  }))

  useEffect(() => {
    propHandleFieldChange('price', formData.price)
    propHandleFieldChange('timing', formData.timing)
    propHandleFieldChange('priceEscort', formData.priceEscort)
    propHandleFieldChange('timingEscort', formData.timingEscort)

    const currentPath = window.location.pathname
    let storageKey = 'myAnnounce'

    if (currentPath === '/dashboard/announce-work/') {
      storageKey = 'myJobOffer'
    } else if (currentPath === '/dashboard/announce-event/') {
      storageKey = 'myEvent'
    } else if (currentPath === '/dashboard/announce-establishment/') {
      storageKey = 'myEstablishment'
    }

    const existingData = JSON.parse(localStorage.getItem(storageKey) || '{}')
    const updatedData = { ...existingData, ...formData }
    localStorage.setItem(storageKey, JSON.stringify(updatedData))
  }, [formData])

  const handleAddPriceClick = (type: 'private' | 'escort') => {
    if (type === 'private') {
      setIsAddingPrivatePrice(true)
    } else {
      setIsAddingEscortPrice(true)
    }
  }

  const handlePriceEntryChange = (key: 'duration' | 'price', value: any) => {
    setNewPriceEntry(prev => ({ ...prev, [key]: value }))
  }

  const handleAddPrice = (type: 'private' | 'escort') => {
    const newPrice = {
      id: uuidv4(),
      duration: newPriceEntry.duration,
      price: newPriceEntry.price,
      escort: type === 'escort',
    }

    if (type === 'escort') {
      setFormData(prevData => ({
        ...prevData,
        priceEscort: [...prevData.priceEscort, newPrice.price],
        timingEscort: [...prevData.timingEscort, newPrice.duration],
      }))
    } else {
      setFormData(prevData => ({
        ...prevData,
        price: [...prevData.price, newPrice.price],
        timing: [...prevData.timing, newPrice.duration],
      }))
    }

    setNewPriceEntry({ duration: '', price: 0 })
    setIsAddingPrivatePrice(false)
    setIsAddingEscortPrice(false)
  }

  const handleDeletePrice = (index: number, type: 'private' | 'escort') => {
    if (type === 'escort') {
      setFormData(prev => {
        const newPriceEscort = [...prev.priceEscort]
        const newTimingEscort = [...prev.timingEscort]
        newPriceEscort.splice(index, 1)
        newTimingEscort.splice(index, 1)
        return {
          ...prev,
          priceEscort: newPriceEscort,
          timingEscort: newTimingEscort,
        }
      })
    } else {
      setFormData(prev => {
        const newPrice = [...prev.price]
        const newTiming = [...prev.timing]
        newPrice.splice(index, 1)
        newTiming.splice(index, 1)
        return {
          ...prev,
          price: newPrice,
          timing: newTiming,
        }
      })
    }
  }

  return (
    <Box>
      <Paper className='border border-anthracite rounded'>
        <Box className='bg-anthracite rounded-t text-white px-5 py-3'>
          <Typography className='text-white p-0 m-0 flex'>
            <span className='uppercase font-bold justify-center p-0 m-0'>
              {t('create-ad.private-prices')}
            </span>
          </Typography>
          <Typography>
            <span className='text-xs'>
              {t('create-ad.private-prices-description')}
            </span>
          </Typography>
        </Box>
        <Box className='p-2 sm:p-4'>
          <MyButton
            variant='blue'
            type='button'
            onClick={() => handleAddPriceClick('private')}
            className='w-full sm:w-auto'
          >
            {t('create-ad.add-private-price')}
          </MyButton>
          {isAddingPrivatePrice && (
            <Box className='mt-2 flex flex-col sm:flex-row gap-2 items-start sm:items-center'>
              <Box className='w-full sm:flex-1'>
                <MySelect
                  label={t('create-ad.select-duration')}
                  options={options}
                  value={newPriceEntry.duration}
                  onChange={value => handlePriceEntryChange('duration', value)}
                  className='w-full'
                  icon={<FaCog />}
                />
              </Box>
              <Box className='w-full sm:flex-1'>
                <MyInput
                  type='number'
                  value={newPriceEntry.price.toString()}
                  label={t('create-ad.price')}
                  onChange={value =>
                    handlePriceEntryChange('price', parseFloat(value))
                  }
                  className='w-full'
                  min={1}
                  max={10000}
                  icon={<MdEuro />}
                />
              </Box>
              <MyButton
                variant='green'
                type='button'
                onClick={() => handleAddPrice('private')}
                disabled={!newPriceEntry.duration || newPriceEntry.price <= 0}
                className='w-full sm:w-auto h-10 mt-2 sm:mt-0'
                sx={{
                  boxShadow: 'none',
                  ':hover': { backgroundColor: '#222121', boxShadow: 'none' },
                }}
              >
                {t('add')}
              </MyButton>
            </Box>
          )}
          {formData.price.length > 0 && (
            <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-4'>
              {formData.price.map((price, index) => (
                <div
                  key={index}
                  className='bg-gray-100/50 rounded-lg p-2 flex justify-between items-center'
                >
                  <div className='flex flex-col sm:flex-row sm:items-center gap-1'>
                    <span className='text-sm'>
                      {formData.timing[index]
                        ? t(`create-ad.${formData.timing[index]}`)
                        : t('priceNotAvailable')}
                    </span>
                    <span className='font-bold text-anthracite text-sm'>
                      {`: ${price}€`}
                    </span>
                  </div>
                  <IconButton
                    onClick={() => handleDeletePrice(index, 'private')}
                    size='small'
                    className='hover:bg-redpink/10'
                  >
                    <DeleteIcon className='text-redpink text-xl' />
                  </IconButton>
                </div>
              ))}
            </div>
          )}
        </Box>
      </Paper>
      {formData.showEscortPrices && (
        <Paper
          className='border border-anthracite rounded'
          sx={{ marginTop: 4 }}
        >
          <Box className='bg-anthracite rounded-t text-white px-5 py-3'>
            <Typography className='text-white p-0 m-0 flex'>
              <span className='uppercase font-bold justify-center p-0 m-0'>
                {t('create-ad.escort-prices')}
              </span>
            </Typography>
            <Typography>
              <span className='text-xs'>
                {t('create-ad.escort-prices-description')}
              </span>
            </Typography>
          </Box>
          <Box className='p-2 sm:p-4'>
            <MyButton
              type='button'
              variant='green'
              onClick={() => handleAddPriceClick('escort')}
              className='w-full sm:w-auto'
            >
              {t('create-ad.add-escort-price')}
            </MyButton>
            {isAddingEscortPrice && (
              <Box className='mt-2 flex flex-col sm:flex-row gap-2 items-start sm:items-center'>
                <Box className='w-full sm:flex-1'>
                  <MySelect
                    label={t('create-ad.select-duration')}
                    options={awayOptions}
                    value={newPriceEntry.duration}
                    onChange={value =>
                      handlePriceEntryChange('duration', value)
                    }
                    className='w-full'
                    icon={<FaCog />}
                  />
                </Box>
                <Box className='w-full sm:flex-1'>
                  <MyInput
                    type='number'
                    value={newPriceEntry.price.toString()}
                    label={t('create-ad.price')}
                    onChange={value =>
                      handlePriceEntryChange('price', parseFloat(value))
                    }
                    className='w-full'
                    min={1}
                    max={10000}
                    icon={<MdEuro />}
                  />
                </Box>
                <Button
                  variant='contained'
                  onClick={() => handleAddPrice('escort')}
                  disabled={!newPriceEntry.duration || newPriceEntry.price <= 0}
                  className='w-full sm:w-auto h-10 mt-2 sm:mt-0'
                  sx={{
                    boxShadow: 'none',
                    ':hover': { backgroundColor: '#222121', boxShadow: 'none' },
                  }}
                >
                  {t('add')}
                </Button>
              </Box>
            )}
            {formData.priceEscort.length > 0 && (
              <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2 mt-4'>
                {formData.priceEscort.map((price, index) => (
                  <div
                    key={index}
                    className='bg-gray-100/50 rounded-lg p-2 flex justify-between items-center'
                  >
                    <div className='flex flex-col sm:flex-row sm:items-center gap-1'>
                      <span className='text-sm'>
                        {formData.timingEscort[index]
                          ? t(`create-ad.${formData.timingEscort[index]}`)
                          : t('priceNotAvailable')}{' '}
                        :{' '}
                      </span>
                      <span className='font-bold text-anthracite text-sm'>
                        {`: ${price}€`}
                      </span>
                    </div>
                    <IconButton
                      onClick={() => handleDeletePrice(index, 'escort')}
                      size='small'
                      className='hover:bg-redpink/10'
                    >
                      <DeleteIcon className='text-redpink text-xl' />
                    </IconButton>
                  </div>
                ))}
              </div>
            )}
          </Box>
        </Paper>
      )}
    </Box>
  )
}

export default UserPricesForm
